import styles from "./EventHistoryItem.module.scss";
import { useNavigate } from "react-router-dom";
import { IEventHistoryItemViewModel } from "../../../models/eventHistoryItemViewModel";
import {
  constructCashdeskHref,
  constructPlannerEventHref,
} from "../../../infrastructure/routes/routePaths";
import { getStatus } from "../../../../components/planner/event/utils/PlannerEventStatusUtility";
import { humanizePaymentType } from "../../../utilities/paymentUtility";
import { SkeletonItem } from "../../skeletons/SkeletonItem";
import { Tag } from "../Tag/Tag";
import { RenderWhen } from "../../../infrastructure/components/RenderWhen";
import { dateUtility } from "../../../infrastructure/utilities/dateUtility";
import {
  HistoryItemKind,
  PaymentTypes,
  UserEventStatus,
} from "../../../enums";
import { classNames } from "primereact/utils";
import { bg } from "date-fns/locale";
import { PriceDisplay } from "../../PriceDisplay/PriceDisplay";

// Cashdesk-history tag accents. The Tag component derives its text colour,
// border and tint from a single JS colour value (see calculateTagColors), so
// these have to be literals here rather than colors.scss variables. Kept named
// and in one place instead of scattered inline magic values.
const ORDER_TAG_COLOR = "#ffe1b3"; // "Продажба" (sale)
const PAYMENT_TAG_COLOR = "#cedef9"; // payment method
const DISCOUNT_TAG_COLOR = "#f9d0d0"; // discount

interface IEventHistoryItemProps {
  event?: IEventHistoryItemViewModel;
  isLoading: boolean;
  actionButtons?: JSX.Element | React.ReactNode;
  onEventClick?: (event: IEventHistoryItemViewModel) => void;
}

export const EventHistoryItem = ({
  event,
  isLoading,
  actionButtons,
  onEventClick: onEventClickProp,
}: IEventHistoryItemProps) => {
  const navigate = useNavigate();

  const isGymVisit = event?.kind === HistoryItemKind.GymVisit;
  const isOrder = event?.kind === HistoryItemKind.Order;

  const onEventClick = () => {
    // Gym rows have no detail page to link to. Suppress here so consumers
    // (e.g. user-profile) that pass an always-navigate onEventClick prop
    // still get the right behavior without each one having to remember.
    if (!event || isGymVisit) {
      return;
    }
    if (onEventClickProp) {
      onEventClickProp(event);
    } else {
      // Order rows open the order in the cashdesk; reservations open the
      // planner event.
      const href = isOrder
        ? constructCashdeskHref({ orderId: event.id })
        : constructPlannerEventHref(event.id);
      navigate(href);
    }
  };

  const {
    color: statusColor,
    label: statusLabel,
    additionalTag,
  } = getStatus(
    event?.isEventPassed ?? false,
    event?.status,
    event?.isCancelled,
    event?.pricePaidInEur,
  );

  const basePaymentTypeLabel = humanizePaymentType(
    event?.paymentType,
    event?.userPlanId,
    event?.planName,
  );

  // Payment types that should show currency (types where we receive money directly)
  // The currency is no longer appended here — the amount itself carries it.
  const paymentTypeLabel = basePaymentTypeLabel;

  const visitsUserLabel =
    !!event?.initialPlanVisits && !!event?.visitsUsed
      ? `${event?.visitsUsed}/${event?.initialPlanVisits}`
      : "N/A";

  // Orders show payment type without a currency suffix — cashdesk sales are
  // always in EUR and the total below already carries the € symbol.
  const orderPaymentTypeLabel = humanizePaymentType(event?.paymentType);

  return (
    <div
      className={classNames(styles.item, {
        [styles.item_nonClickable]: isGymVisit,
      })}
      onClick={onEventClick}
    >
      <div className={styles.item_content}>
        <div className={styles.item_content_left}>
          <div className={styles.item_content_left_title}>
            <SkeletonItem
              isLoading={isLoading}
              value={event?.eventName}
              width={250}
            />
          </div>
          <RenderWhen condition={!!event?.usedBy}>
            <div className={styles.item_content_left_usedBy}>
              {event?.usedBy}
            </div>
          </RenderWhen>
          <SkeletonItem
            isLoading={isLoading}
            width={200}
            value={
              isOrder ? (
                <div className={styles.item_content_left_sub}>
                  <Tag label="Продажба" backgroundColor={ORDER_TAG_COLOR} />
                  <RenderWhen condition={!!orderPaymentTypeLabel}>
                    <Tag
                      label={orderPaymentTypeLabel!}
                      backgroundColor={PAYMENT_TAG_COLOR}
                    />
                  </RenderWhen>
                  <RenderWhen condition={!!event?.discountPercent}>
                    <Tag
                      label={`Отстъпка ${event?.discountPercent}%`}
                      backgroundColor={DISCOUNT_TAG_COLOR}
                    />
                  </RenderWhen>
                </div>
              ) : (
                <div className={styles.item_content_left_sub}>
                  <Tag label={statusLabel} backgroundColor={statusColor} />
                  <RenderWhen condition={!!additionalTag}>
                    <Tag
                      label={additionalTag?.label ?? ""}
                      backgroundColor={additionalTag?.color ?? ""}
                    />
                  </RenderWhen>
                  <RenderWhen condition={!!paymentTypeLabel}>
                    <Tag label={paymentTypeLabel!} backgroundColor="#cedef9" />
                  </RenderWhen>
                </div>
              )
            }
          />
        </div>

        <div className={styles.item_content_right}>{actionButtons}</div>
      </div>

      <div className={styles.item_content_footer}>
        <SkeletonItem
          isLoading={isLoading}
          width={220}
          value={
            <div className={styles.item_content_footer_left}>
              <div>
                <span className={styles.item_content_footer_left_name}>
                  {isGymVisit ? "Влизане: " : "Дата: "}
                </span>
                <span className={styles.item_content_footer_left_value}>
                  {dateUtility.formatDate(
                    event?.date,
                    "eee, dd/MM/yyyy, HH:mm",
                    bg,
                  )}
                </span>
              </div>
              <RenderWhen condition={isGymVisit && !!event?.checkedOutAt}>
                <div>
                  <span className={styles.item_content_footer_left_name}>
                    Излизане:{" "}
                  </span>
                  <span className={styles.item_content_footer_left_value}>
                    {dateUtility.formatDate(
                      event?.checkedOutAt,
                      "eee, dd/MM/yyyy, HH:mm",
                      bg,
                    )}
                  </span>
                </div>
              </RenderWhen>
              <RenderWhen condition={isGymVisit && !event?.checkedOutAt}>
                <div>
                  <span className={styles.item_content_footer_left_name}>
                    Все още в залата
                  </span>
                </div>
              </RenderWhen>
            </div>
          }
        />
        <SkeletonItem
          width="20%"
          isLoading={isLoading}
          value={
            isOrder ? (
              <div className={styles.item_content_footer_right}>
                <span className={styles.item_content_footer_right_pricePaid}>
                  <PriceDisplay
                    minPrice={event?.pricePaid ?? 0}
                    currencyId={event?.currencyId}
                  />
                </span>
              </div>
            ) : (
              <div className={styles.item_content_footer_right}>
                <RenderWhen
                  condition={
                    !!event?.pricePaid &&
                    (event.status === UserEventStatus.Arrived ||
                      event.status === UserEventStatus.OnlyPaid ||
                      event.status === UserEventStatus.Confirmed)
                  }
                >
                  <span className={styles.item_content_footer_right_pricePaid}>
                    <PriceDisplay
                      minPrice={event?.pricePaid ?? 0}
                      currencyId={event?.currencyId}
                    />
                  </span>
                </RenderWhen>
                <span className={styles.item_content_footer_right_visits}>
                  <RenderWhen
                    condition={
                      event?.paymentType === PaymentTypes.Plan &&
                      !!event?.initialPlanVisits
                    }
                  >
                    | {visitsUserLabel}
                  </RenderWhen>
                </span>
              </div>
            )
          }
        />
      </div>
    </div>
  );
};
